import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Calendar, Clock, MapPin, Check, X, HelpCircle, Share, Copy, Users, Mail, Phone, User, Settings, Lightbulb } from "lucide-react";
import { Event, EventOption, Guest } from "@/types/event";
import { CountdownTimer } from "./CountdownTimer";
import { CalendarSyncPrompt } from "./CalendarSyncPrompt";
import { AvailabilityIndicator, AvailabilityStatus } from "./AvailabilityIndicator";
import { SmartVotingFeatures } from "./SmartVotingFeatures";
import { CustomRSVPForm } from "./CustomRSVPForm";
import { useToast } from "@/hooks/use-toast";
import { eventService } from "@/services/eventService";
import { analyticsService } from "@/services/analyticsService";
import { User as SupabaseUser } from "@supabase/supabase-js";
import { useSmartVoting } from "@/hooks/useSmartVoting";
import { useSmartAvailabilitySync } from "@/hooks/useSmartAvailabilitySync";
import { useCustomRSVP } from "@/hooks/useCustomRSVP";
import { GuestSuggestionDialog } from "./GuestSuggestionDialog";
import { InlineRSVPQuestions } from "./InlineRSVPQuestions";
interface EventVotingProps {
  event: Event;
  onVoteSubmitted: () => void;
  onBack: () => void;
  authenticatedUser: SupabaseUser;
}
export const EventVoting: React.FC<EventVotingProps> = ({
  event,
  onVoteSubmitted,
  onBack,
  authenticatedUser
}) => {
  const { toast } = useToast();
  const { isFeatureEnabled: isSmartVotingEnabled } = useSmartVoting();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [unavailableOptions, setUnavailableOptions] = useState<string[]>([]);
  const [maybeOptions, setMaybeOptions] = useState<string[]>([]);
  const [isVotingClosed, setIsVotingClosed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentGuest, setCurrentGuest] = useState<Guest | null>(null);
  const { isFeatureEnabled: isSmartAvailabilitySyncEnabled } = useSmartAvailabilitySync({ 
    eventId: event.id, 
    guestId: currentGuest?.id 
  });
  const [availabilityData, setAvailabilityData] = useState<Record<string, AvailabilityStatus>>({});
  const [preventBusyVoting, setPreventBusyVoting] = useState(false);
  const [showSmartFeatures, setShowSmartFeatures] = useState(true);
  const [showGroupHeatmap, setShowGroupHeatmap] = useState(false);
  const [showCustomRSVP, setShowCustomRSVP] = useState(false);
  const { rsvpForm, submitResponse } = useCustomRSVP(event.id);
  const [rsvpAnswers, setRsvpAnswers] = useState<Record<string, string | string[]>>({});
  const [showRsvpErrors, setShowRsvpErrors] = useState(false);
  const voteStartedAtRef = useRef<number | null>(null);

  // Start the time-to-first-vote timer once the guest is identified
  useEffect(() => {
    if (currentGuest && !currentGuest.hasVoted && voteStartedAtRef.current === null) {
      voteStartedAtRef.current = Date.now();
    }
  }, [currentGuest]);

  // Required RSVP questions missing (for sticky submit label)
  const missingRequiredCount = useMemo(() => {
    if (!rsvpForm?.questions?.length) return 0;
    return rsvpForm.questions.filter((q: any) =>
      q.required &&
      (!rsvpAnswers[q.id] || (Array.isArray(rsvpAnswers[q.id]) && (rsvpAnswers[q.id] as string[]).length === 0))
    ).length;
  }, [rsvpForm, rsvpAnswers]);
  const getIconForOption = (type: string) => {
    switch (type) {
      case 'date':
        return <Calendar className="w-4 h-4" />;
      case 'time':
        return <Clock className="w-4 h-4" />;
      case 'location':
        return <MapPin className="w-4 h-4" />;
      default:
        return null;
    }
  };
  const formatOptionValue = (option: EventOption) => {
    switch (option.type) {
      case 'date':
        return new Date(option.value).toLocaleDateString();
      case 'time':
        return new Date(`2000-01-01T${option.value}`).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      case 'location':
        return option.value;
      default:
        return option.value;
    }
  };
  const triggerHaptic = () => {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      try { navigator.vibrate(10); } catch { /* noop */ }
    }
  };

  const handleOptionClick = (optionId: string, action: 'prefer' | 'unavailable' | 'maybe') => {
    // Check if voting on busy times is prevented
    if (preventBusyVoting && action === 'prefer') {
      const availability = availabilityData[optionId];
      if (availability === 'busy') {
        toast({
          title: "Cannot vote for busy time",
          description: "You have calendar conflicts during this time. Please choose a different option or disable conflict prevention.",
          variant: "destructive"
        });
        return;
      }
    }

    triggerHaptic();

    if (action === 'prefer') {
      setSelectedOptions(prev => prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]);
      setUnavailableOptions(prev => prev.filter(id => id !== optionId));
      setMaybeOptions(prev => prev.filter(id => id !== optionId));
    } else if (action === 'unavailable') {
      setUnavailableOptions(prev => prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]);
      setSelectedOptions(prev => prev.filter(id => id !== optionId));
      setMaybeOptions(prev => prev.filter(id => id !== optionId));
    } else {
      setMaybeOptions(prev => prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]);
      setSelectedOptions(prev => prev.filter(id => id !== optionId));
      setUnavailableOptions(prev => prev.filter(id => id !== optionId));
    }
  };

  const handleAvailabilityChange = (optionId: string, status: AvailabilityStatus) => {
    setAvailabilityData(prev => ({
      ...prev,
      [optionId]: status
    }));
  };

  const handleQuickVoteAvailable = () => {
    toast({
      title: "Smart votes applied!",
      description: "Selected all your available time slots based on calendar analysis.",
    });
  };

  const handleSuggestedOptionsSelect = (optionIds: string[]) => {
    setSelectedOptions(optionIds);
    // Clear any conflicting unavailable votes
    setUnavailableOptions(prev => prev.filter(id => !optionIds.includes(id)));
  };

  // Effect to identify guest using authenticated user's email
  useEffect(() => {
    const identifyGuest = async () => {
      if (!authenticatedUser?.email) {
        return;
      }
      try {
        // Find the guest by the authenticated user's email
        const guest = event.guests.find(g => g.email === authenticatedUser.email);
        if (guest) {
          setCurrentGuest(guest);
        }
      } catch (error) {
        console.error("Error identifying guest:", error);
      }
    };
    identifyGuest();
  }, [authenticatedUser, event.guests]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isVotingClosed || isSubmitting) return;
    if (!currentGuest) {
      toast({
        title: "Guest not found",
        description: "You don't appear to be on the guest list for this event.",
        variant: "destructive"
      });
      return;
    }

    // Validate RSVP questions if they exist
    if (rsvpForm && rsvpForm.questions && rsvpForm.questions.length > 0) {
      const requiredQuestions = rsvpForm.questions.filter((q: any) => q.required);
      const missingRequired = requiredQuestions.filter((q: any) => 
        !rsvpAnswers[q.id] || (Array.isArray(rsvpAnswers[q.id]) && rsvpAnswers[q.id].length === 0)
      );

      if (missingRequired.length > 0) {
        setShowRsvpErrors(true);
        toast({
          title: "Missing required information",
          description: "Please fill in all required fields before submitting.",
          variant: "destructive"
        });
        // Scroll to RSVP section
        setTimeout(() => {
          const rsvpSection = document.querySelector('[data-rsvp-questions]');
          if (rsvpSection) {
            rsvpSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Prepare votes data
      const votes = [
        ...selectedOptions.map(optionId => ({ optionId, voteType: 'prefer' as const })),
        ...unavailableOptions.map(optionId => ({ optionId, voteType: 'unavailable' as const })),
        ...maybeOptions.map(optionId => ({ optionId, voteType: 'maybe' as const })),
      ];

      // Submit votes
      await eventService.submitVotes(event.id, currentGuest.id, votes);

      // Submit RSVP responses if they exist
      if (rsvpForm && rsvpForm.questions && rsvpForm.questions.length > 0 && Object.keys(rsvpAnswers).length > 0) {
        await submitResponse(currentGuest.id, rsvpAnswers);
      }

      // Track vote analytics
      // Track vote analytics (include time-to-first-vote on the first submit)
      const isFirstVote = !currentGuest.hasVoted;
      const durationMs =
        isFirstVote && voteStartedAtRef.current !== null
          ? Date.now() - voteStartedAtRef.current
          : undefined;
      analyticsService.trackVote(event.id, authenticatedUser.id, currentGuest.id, { durationMs });

      toast({
        title: "Response submitted successfully!",
        description: "Thank you for voting. You can change your response anytime before the deadline."
      });
      onVoteSubmitted();
    } catch (error) {
      console.error("Error submitting response:", error);
      toast({
        title: "Failed to submit response",
        description: "There was an error submitting your response. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleVotingExpired = () => {
    setIsVotingClosed(true);
    toast({
      title: "Voting has closed",
      description: "The voting deadline has passed.",
      variant: "destructive"
    });
  };
  const copyShareableLink = () => {
    // Use the vote-meta edge function URL directly so social/messaging
    // crawlers (iMessage, Slack, WhatsApp, Twitter, FB) get rich OG previews.
    // The function emits a meta-refresh that bounces real browsers to the SPA.
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const fullLink = `${supabaseUrl}/functions/v1/vote-meta?event=${encodeURIComponent(event.shareableLink)}`;
    navigator.clipboard.writeText(fullLink);
    toast({
      title: "Link copied!",
      description: "Shareable link has been copied to your clipboard"
    });
  };
  const groupedOptions = {
    date: event.options.filter(opt => opt.type === 'date'),
    time: event.options.filter(opt => opt.type === 'time'),
    location: event.options.filter(opt => opt.type === 'location')
  };

  const handleCustomRSVPSubmitted = () => {
    setShowCustomRSVP(false);
    onVoteSubmitted();
  };
  return <div className="min-h-screen bg-gradient-background pb-24 sm:pb-0">
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">

        <div className="max-w-4xl mx-auto space-y-4 sm:space-y-8">
          {/* Event Header */}
          <Card className="shadow-medium border-0">
            <CardHeader className="text-center p-4 sm:p-6">
              <CardTitle className="text-xl sm:text-2xl md:text-3xl bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {event.name}
              </CardTitle>
              <CardDescription className="text-base sm:text-lg mt-1 sm:mt-2">
                Hosted by {event.hostName}
              </CardDescription>
              {event.description && <p className="text-muted-foreground mt-2 text-sm sm:text-base">{event.description}</p>}
            </CardHeader>
          </Card>

          {/* Countdown Timer */}
          {event.deadline && !isVotingClosed && <div>
              <CountdownTimer deadline={event.deadline} onExpired={handleVotingExpired} />
            </div>}

          {/* Guest Information & Share */}
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-6">
            {/* Guest Stats */}
            <Card className="shadow-glass border-0 bg-gradient-card backdrop-blur-glass">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm sm:text-base truncate">Invited Guests</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {event.guests.length} people
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Share Event */}
            <Card className="shadow-glass border-0 bg-gradient-card backdrop-blur-glass">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-success/10 flex items-center justify-center shrink-0">
                      <Share className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm sm:text-base truncate">Share</p>
                      <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
                        Invite more
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={copyShareableLink} className="shrink-0 touch-manipulation">
                    <Copy className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Copy</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Voting Form */}
          <Card className="shadow-medium border-0">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                Cast Your Vote
              </CardTitle>
              <CardDescription className="text-sm sm:text-base">
                Select your preferred options and mark any you're unavailable for
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-8 pb-28 sm:pb-0">
                {/* Guest Identification */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Voting as</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      You are voting as an invited guest with the following information:
                    </p>
                  </div>
                  
                  {currentGuest && <div className="p-4 border border-accent/20 rounded-lg bg-green-100">
                      <div className="flex items-center gap-2 text-sm text-accent-foreground mb-3">
                        <Check className="w-4 h-4 text-accent" />
                        <span className="font-semibold text-slate-950">Authenticated as invited guest</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <span>{authenticatedUser.email}</span>
                        </div>
                        {currentGuest.name && <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>{currentGuest.name}</span>
                          </div>}
                        {currentGuest.phone && <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <span>{currentGuest.phone}</span>
                          </div>}
                      </div>
                      {currentGuest.hasVoted && <Badge variant="secondary" className="mt-3">Already voted</Badge>}
                     </div>}
                </div>

                {/* Calendar Sync Prompt */}
                {currentGuest && isSmartAvailabilitySyncEnabled && (
                  <div className="space-y-4">
                    <CalendarSyncPrompt 
                      eventId={event.id}
                      guestId={currentGuest.id}
                      onSyncComplete={(provider) => {
                        toast({
                          title: "Calendar Connected",
                          description: `Successfully connected your ${provider} calendar for availability checking.`,
                        });
                      }}
                    />
                  </div>
                )}

                {/* Smart Voting Features */}
                {currentGuest && isSmartVotingEnabled && showSmartFeatures && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        Smart Voting Options
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowSmartFeatures(!showSmartFeatures)}
                      >
                        {showSmartFeatures ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="prevent-busy"
                          checked={preventBusyVoting}
                          onCheckedChange={setPreventBusyVoting}
                        />
                        <Label htmlFor="prevent-busy" className="text-sm">
                          Prevent voting on busy times
                        </Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="show-heatmap"
                          checked={showGroupHeatmap}
                          onCheckedChange={setShowGroupHeatmap}
                        />
                        <Label htmlFor="show-heatmap" className="text-sm">
                          Show group availability
                        </Label>
                      </div>
                    </div>

                    <SmartVotingFeatures
                      event={event}
                      guestId={currentGuest.id}
                      availabilityData={availabilityData}
                      onQuickVoteAvailable={handleQuickVoteAvailable}
                      onSuggestedOptionsSelect={handleSuggestedOptionsSelect}
                      showHeatmap={showGroupHeatmap}
                    />
                  </div>
                )}

                {/* Voting Sections */}
                {Object.entries(groupedOptions).map(([type, options]) => {
                if (options.length === 0) return null;
                return <div key={type} className="space-y-3 sm:space-y-4">
                      <div className="flex items-center gap-2">
                        {getIconForOption(type)}
                        <h3 className="text-base sm:text-lg font-semibold capitalize">{type}s</h3>
                      </div>
                      
                      <div className="grid gap-2 sm:gap-3">
                        {options.map(option => {
                          const isSelected = selectedOptions.includes(option.id);
                          const isUnavailable = unavailableOptions.includes(option.id);
                          const isMaybe = maybeOptions.includes(option.id);
                          const availability = availabilityData[option.id];
                          const isBlocked = preventBusyVoting && availability === 'busy';
                          
                          return (
                            <div key={option.id} className={`p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 ${
                              isSelected ? 'border-l-[6px] border-accent bg-accent/10' : 
                              isUnavailable ? 'border-l-[6px] border-destructive bg-destructive/10' : 
                              isMaybe ? 'border-l-[6px] border-warning bg-warning/10' :
                              isBlocked ? 'border-destructive/50 bg-destructive/5 opacity-60' :
                              'border-border hover:border-primary/50'
                            }`}>
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                  <AvailabilityIndicator
                                    eventId={event.id}
                                    guestId={currentGuest?.id}
                                    optionId={option.id}
                                    type={option.type as 'date' | 'time' | 'location'}
                                    value={formatOptionValue(option)}
                                    showDetails={true}
                                    enableLiveCheck={true}
                                    onStatusChange={(status) => handleAvailabilityChange(option.id, status)}
                                  />
                                </div>
                                <div className="flex flex-col min-[380px]:flex-row gap-2 w-full sm:w-auto">
                                  <Button 
                                    type="button" 
                                    variant={isSelected ? "success" : "outline"} 
                                    size="sm" 
                                    onClick={() => handleOptionClick(option.id, 'prefer')} 
                                    className="flex-1 sm:flex-none sm:min-w-[110px] min-h-[48px] touch-manipulation" 
                                    disabled={isVotingClosed || isBlocked}
                                    aria-pressed={isSelected}
                                    aria-label={isSelected ? 'Remove preferred vote' : 'Mark as preferred'}
                                  >
                                    <Check className="w-4 h-4 mr-1.5" />
                                    <span>{isSelected ? 'Preferred' : 'Prefer'}</span>
                                  </Button>
                                  <Button 
                                    type="button" 
                                    variant={isMaybe ? "default" : "outline"} 
                                    size="sm" 
                                    onClick={() => handleOptionClick(option.id, 'maybe')} 
                                    className={`flex-1 sm:flex-none sm:min-w-[100px] min-h-[48px] touch-manipulation ${isMaybe ? 'bg-warning text-warning-foreground hover:bg-warning/90' : ''}`}
                                    disabled={isVotingClosed}
                                    aria-pressed={isMaybe}
                                    aria-label={isMaybe ? 'Remove maybe vote' : 'Mark as maybe'}
                                  >
                                    <HelpCircle className="w-4 h-4 mr-1.5" />
                                    <span>Maybe</span>
                                  </Button>
                                  <Button 
                                    type="button" 
                                    variant={isUnavailable ? "destructive" : "outline"} 
                                    size="sm" 
                                    onClick={() => handleOptionClick(option.id, 'unavailable')} 
                                    className="flex-1 sm:flex-none sm:min-w-[130px] min-h-[48px] touch-manipulation" 
                                    disabled={isVotingClosed}
                                    aria-pressed={isUnavailable}
                                    aria-label={isUnavailable ? 'Remove unavailable vote' : 'Mark as unavailable'}
                                  >
                                    <X className="w-4 h-4 mr-1.5" />
                                    <span>{isUnavailable ? 'Unavailable' : 'Not Available'}</span>
                                  </Button>
                                </div>
                              </div>
                              
                              {isBlocked && (
                                <div className="mt-2 text-xs text-destructive">
                                  Calendar conflict detected - voting prevented
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>;
              })}

                {/* Voting Summary */}
                {(selectedOptions.length > 0 || unavailableOptions.length > 0 || maybeOptions.length > 0) && <div className="bg-gradient-secondary p-3 sm:p-4 rounded-lg">
                    <h4 className="font-semibold mb-3 text-sm sm:text-base">Your Votes Summary</h4>
                    <div className="space-y-2">
                      {selectedOptions.length > 0 && <div className="flex flex-wrap gap-2">
                          <span className="text-sm font-medium">Preferred:</span>
                          {selectedOptions.map(optionId => {
                      const option = event.options.find(opt => opt.id === optionId);
                      return option ? <Badge key={optionId} variant="secondary" className="bg-accent/20 text-accent-foreground">
                                {formatOptionValue(option)}
                              </Badge> : null;
                    })}
                        </div>}
                      {maybeOptions.length > 0 && <div className="flex flex-wrap gap-2">
                          <span className="text-sm font-medium">Maybe:</span>
                          {maybeOptions.map(optionId => {
                      const option = event.options.find(opt => opt.id === optionId);
                      return option ? <Badge key={optionId} className="bg-warning/20 text-warning-foreground border border-warning/40">
                                {formatOptionValue(option)}
                              </Badge> : null;
                    })}
                        </div>}
                      {unavailableOptions.length > 0 && <div className="flex flex-wrap gap-2">
                          <span className="text-sm font-medium">Unavailable:</span>
                          {unavailableOptions.map(optionId => {
                      const option = event.options.find(opt => opt.id === optionId);
                      return option ? <Badge key={optionId} variant="destructive">
                                {formatOptionValue(option)}
                              </Badge> : null;
                    })}
                        </div>}
                    </div>
                  </div>}

                {/* Inline RSVP Questions */}
                {rsvpForm && rsvpForm.questions && rsvpForm.questions.length > 0 && (
                  <div data-rsvp-questions>
                    <InlineRSVPQuestions
                      questions={rsvpForm.questions}
                      answers={rsvpAnswers}
                      onAnswerChange={(questionId, value) => {
                        setRsvpAnswers(prev => ({ ...prev, [questionId]: value }));
                        setShowRsvpErrors(false);
                      }}
                      showErrors={showRsvpErrors}
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  className="hidden sm:flex w-full touch-manipulation text-sm sm:text-base"
                  size="lg"
                  disabled={isVotingClosed || isSubmitting || !currentGuest}
                >
                  {isSubmitting ? 'Submitting...' : isVotingClosed ? 'Voting Closed' : currentGuest?.hasVoted ? 'Update My Response' : rsvpForm?.questions?.length > 0 ? 'Submit Vote & Information' : 'Submit My Votes'}
                </Button>

                {/* Sticky mobile submit bar — always visible while scrolling */}
                <div className="sm:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-lg">
                  <Button
                    type="submit"
                    className="w-full touch-manipulation min-h-[52px] text-base"
                    size="lg"
                    disabled={isVotingClosed || isSubmitting || !currentGuest}
                  >
                    {isSubmitting
                      ? 'Submitting...'
                      : isVotingClosed
                        ? 'Voting Closed'
                        : missingRequiredCount > 0
                          ? `Complete ${missingRequiredCount} required field${missingRequiredCount === 1 ? '' : 's'}`
                          : currentGuest?.hasVoted
                            ? 'Update My Response'
                            : rsvpForm?.questions?.length > 0
                              ? 'Submit Vote & Info'
                              : 'Submit My Votes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
          

          {/* Guest Suggestion Feature */}
          {event.allowGuestSuggestions && currentGuest && (
            <Card className="shadow-medium border-0 bg-gradient-to-r from-primary/5 to-accent/5">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Lightbulb className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold">Suggest an Alternative</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Have a better date, time, or location in mind?
                    </p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                  The host has enabled guest suggestions. You can propose alternative options for this event.
                </p>
                <GuestSuggestionDialog
                  eventId={event.id}
                  guestId={currentGuest.id}
                  onSuggestionSubmitted={() => {
                    // Refresh the event data to show new suggestions
                    window.location.reload();
                  }}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>;
};